import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, Loader2, Printer, Upload } from 'lucide-react'

import { api } from '@/api/client'
import { useAuth, type Company } from '@/stores/auth'
import { useToast } from '@/stores/toast'
import { useTabField } from '@/stores/tabState'
import { useFileDownload } from '@/lib/useFileDownload'
import { inputCls } from '@/components/Field'
import { cn, formatNum, todayISO } from '@/lib/utils'

interface MzRow {
  store_code: string
  store: string
  item_code: string
  item_name: string
  unit: string
  qty: number
  route: string
  origin: string
  matched: boolean
  has_po?: boolean    // 날짜 방식: 발주서 원문이 저장된 줄인지(없으면 ERP 값으로 대신)
}

interface MzResp { rows: MzRow[]; total: number; unmatched: number; no_po?: number }

const API = '/api/purchase/mz-center-label'

/** 회사관리 > 라벨 > 엠즈 센터 — 라벨(상품명·수량·출고단위·원산지·거래처명·거래처코드·기준노선).
 *  날짜로 불러오기(기본): 발주서 변환 때 저장한 원문으로 그날 출고분 라벨.
 *  파일 올리기: 센터 발주서 파일에서 바로(원문 저장 전 날짜·예외용, 저장하지 않음). */
export function MzCenterLabelTab() {
  const push = useToast((s) => s.push)
  const [mode, setMode] = useTabField<'date' | 'file'>('mz-label', 'mode', 'date')
  const [date, setDate] = useTabField('mz-label', 'date', todayISO())
  const [pickedCompany, setCompany] = useTabField<number | null>('mz-label', 'company', null)
  const { downloading: dlDate, download } = useFileDownload()

  const { data: companies = [] } = useQuery({
    queryKey: ['companies', ''],
    queryFn: () => api<Company[]>('/api/companies?q='),
  })
  // 기본 = 이름에 '엠즈'가 든 회사(센터 라벨 대상). 바꾸면 그 선택을 기억.
  const companyId = pickedCompany ?? companies.find((c) => c.name.includes('엠즈'))?.id ?? companies[0]?.id ?? null
  const dateQs = `date=${date}&company=${companyId}`
  const { data: dateData, isFetching } = useQuery({
    queryKey: ['mz-label-date', dateQs],
    queryFn: () => api<MzResp>(`${API}?${dateQs}`),
    enabled: mode === 'date' && !!date && !!companyId,
  })

  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [fileData, setFileData] = useState<MzResp | null>(null)
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)

  function form(f: File) {
    const fd = new FormData()
    fd.append('file', f)
    return fd
  }

  async function onPick(f: File | undefined) {
    if (!f) return
    setLoading(true)
    try {
      setFileData(await api<MzResp>(API, { method: 'POST', body: form(f) }))
      setFile(f)
    } catch (e) {
      setFileData(null); setFile(null)
      push(`읽기 실패: ${e instanceof Error ? e.message : e}`, 'error', 6000)
    } finally {
      setLoading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  async function onDownloadFile() {
    if (!file) return
    setDownloading(true)
    try {
      const token = useAuth.getState().token
      const res = await fetch(`${API}?download=1`, {
        method: 'POST', body: form(file),
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const url = URL.createObjectURL(await res.blob())
      const a = document.createElement('a')
      a.href = url
      a.download = `엠즈센터라벨_${file.name.replace(/\.[^.]+$/, '')}.xlsx`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      push('엑셀 다운로드 완료', 'success')
    } catch (e) {
      push(`다운로드 실패: ${e instanceof Error ? e.message : e}`, 'error', 6000)
    } finally {
      setDownloading(false)
    }
  }

  const isDate = mode === 'date'
  const data = isDate ? dateData : fileData
  const rows = data?.rows ?? []
  const busy = isDate ? dlDate : downloading
  const title = isDate
    ? `${companies.find((c) => c.id === companyId)?.name ?? ''} · ${date}`
    : (file?.name ?? '')

  return (
    <div id="mz-label-print" className="max-w-6xl mx-auto p-6">
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #mz-label-print, #mz-label-print * { visibility: visible !important; }
        #mz-label-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
        #mz-label-print .no-print { display: none !important; }
        @page { size: A4 portrait; margin: 10mm; }
      }`}</style>
      <h3 className="hidden print:block text-lg font-bold mb-3">엠즈(센터) 라벨 — {title}</h3>

      <div className="no-print bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap shadow-sm mb-3">
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm">
          {([['date', '날짜로 불러오기'], ['file', '파일 올리기']] as const).map(([k, lbl]) => (
            <button key={k} onClick={() => setMode(k)}
                    className={cn('px-3 py-1.5 font-semibold',
                      mode === k ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50')}>
              {lbl}
            </button>
          ))}
        </div>
        {isDate ? (
          <>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   className={inputCls + ' w-44 tabular-nums'}/>
            <select value={companyId ?? ''} onChange={(e) => setCompany(Number(e.target.value))}
                    className={inputCls + ' w-52'}>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {isFetching && <Loader2 className="w-4 h-4 animate-spin text-slate-400"/>}
          </>
        ) : (
          <>
            <input ref={inputRef} type="file" accept=".xls,.xlsx" className="hidden"
                   onChange={(e) => void onPick(e.target.files?.[0])}/>
            <button onClick={() => inputRef.current?.click()} disabled={loading}
                    className="flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
              센터 발주서 올리기
            </button>
            <span className="text-xs text-slate-500 truncate max-w-xs">{file?.name ?? '센터 웹에서 받은 발주서 엑셀(.xls)'}</span>
          </>
        )}
        {data && (
          <span className="text-xs tabular-nums text-slate-500">
            {formatNum(data.total)}줄
            {data.unmatched > 0 && <span className="text-amber-600 ml-1">· 원산지 없음 {data.unmatched}</span>}
            {!!data.no_po && <span className="text-amber-600 ml-1">· 발주서 원문 없음 {data.no_po}</span>}
          </span>
        )}
        <button onClick={() => window.print()} disabled={!rows.length}
                className="ml-auto flex items-center gap-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
          <Printer className="w-4 h-4"/> 인쇄
        </button>
        <button onClick={() => { if (isDate) void download(`${API}?${dateQs}&download=1`, `엠즈센터라벨_${date}.xlsx`); else void onDownloadFile() }}
                disabled={!rows.length || busy}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
          엑셀 다운로드
        </button>
      </div>

      {isDate && !!data?.no_po && (
        <p className="no-print text-xs text-amber-700 mb-2">
          발주서 원문이 없는 줄(직접 입력했거나 이 기능 배포 전에 변환한 출고분)은 ERP 품명·거래처명으로 표시되고
          거래처코드·기준노선이 빈칸입니다. 그 날짜는 "파일 올리기"로 뽑으세요.
        </p>
      )}

      {!rows.length && !loading && !isFetching && (
        <div className="no-print text-center py-16 text-slate-400 text-sm bg-white rounded-xl border border-slate-200">
          {isDate
            ? '이 날짜에 출고가 없습니다. 센터 발주서를 발주서 변환으로 등록하면 여기서 날짜로 불러올 수 있습니다.'
            : <>센터 발주서를 올리면 라벨(상품명 · 수량 · 출고단위 · 원산지 · 거래처명 · 거래처코드 · 기준노선)이 만들어집니다.<br/>
                원산지는 ERP 품목 규격을 씁니다. 파일은 저장되지 않습니다.</>}
        </div>
      )}

      {!!rows.length && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden print:border-0 print:rounded-none">
          <table className="w-full text-sm print:text-xs border-collapse print:[&_td]:border print:[&_td]:border-slate-400 print:[&_th]:border print:[&_th]:border-slate-500">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">상품명</th>
                <th className="px-3 py-2 text-right font-medium w-14">수량</th>
                <th className="px-3 py-2 text-left font-medium w-16">출고단위</th>
                <th className="px-3 py-2 text-left font-medium w-20">원산지</th>
                <th className="px-3 py-2 text-left font-medium">거래처명</th>
                <th className="px-3 py-2 text-left font-medium w-20">거래처코드</th>
                <th className="px-3 py-2 text-left font-medium">기준노선</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={cn('border-t border-slate-100',
                  (!r.matched || r.has_po === false) && 'bg-amber-50 print:bg-transparent')}>
                  <td className="px-3 py-1.5 text-slate-800">{r.item_name}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatNum(r.qty)}</td>
                  <td className="px-3 py-1.5 text-slate-600">{r.unit}</td>
                  <td className="px-3 py-1.5 text-slate-600">
                    {r.origin || (!r.matched && <span className="text-[11px] text-amber-600 no-print">품목 매칭 없음</span>)}
                  </td>
                  <td className="px-3 py-1.5 text-slate-800">{r.store}</td>
                  <td className="px-3 py-1.5 tabular-nums text-slate-600">{r.store_code}</td>
                  <td className="px-3 py-1.5 text-slate-600">{r.route}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
